import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ContactData} from '../../../types/organization/contact-data';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {AddContactService} from '../../../services/contact/add-contact.service';
import {LocationData} from '../../../types/location/location-data';
import {ENTRY_STATES} from '../../../types/entry/EntryStatesEnum';
import {LocationRestService} from '../../../services/location/location-rest.service';
import {CountryRestService} from '../../../services/common/country-rest.service';

@Component({
  selector: 'wsd-add-contact',
  templateUrl: './add-contact.component.html',
  styleUrls: ['./add-contact.component.css'],
})
export class AddContactComponent implements OnInit {

  @Output()
  onSave = new EventEmitter<any>();

  @Input()
  includingPostAddress: boolean;

  @Input()
  countryId: number;

  @Input()
  contacts: ContactData[];

  @Input()
  isOrganization: boolean;

  contactForm: UntypedFormGroup;

  contact: ContactData;

  address: LocationData;

  display: boolean = false;

  locationTypes = [];

  internationalDialingCode: string = '';

  constructor(private addContactService: AddContactService,
              private locationService: LocationRestService,
              private fb: UntypedFormBuilder,
              private countryService: CountryRestService) {
  }

  ngOnInit() {
    this.locationService.getLocationTypes().subscribe(types => {
      this.locationTypes = types;
    });
    this.contactForm = this.fb.group({
      contactType: ['', Validators.required],
      value: [''],
      contactVisibility: ['', Validators.required],
      preferred: [false],
      address1: [''],
      address2: [''],
      address3: [''],
      address4: [''],
      addressZip: [''],
      addressCity: [''],
      addressCountry: [''],
    });
    this.addContactService.messageSent$.subscribe(
      (contact: ContactData) => {
        this.contact = {$state: contact.$state};
        this.address = {$state: contact.$state};
        this.contactForm.reset();
        if (contact.$state === ENTRY_STATES.NEW) {
          this.contactForm.get('contactType').enable();
        } else {
          this.contactForm.get('contactType').disable();
          if (contact.preferred === true) {
            this.contactForm.get('preferred').enable();
          } else {
            this.checkIfIsMorePreferredContactType(contact.type);
          }
        }
        if (contact.hasOwnProperty('type')) {
          this.init(contact);
        } else {
          this.initAddress(contact);
        }
        this.display = true;
      }
    );
  }

  private init(contact: ContactData) {
    this.contact = contact;
    this.contactForm.get('contactType').setValue(this.contact.type);
    this.contactForm.get('value').setValue(this.contact.value);
    this.contactForm.get('contactVisibility').setValue(this.contact.visibleInDirectory);
    this.contactForm.get('preferred').setValue(this.contact.preferred);
  }

  private initAddress(address: LocationData) {
    this.address = address;
    this.contactForm.get('contactType').setValue(this.address.locationType);
    this.contactForm.get('address1').setValue(this.address.address1);
    this.contactForm.get('address2').setValue(this.address.address2);
    this.contactForm.get('address3').setValue(this.address.address3);
    this.contactForm.get('address4').setValue(this.address.address4);
    this.contactForm.get('addressZip').setValue(this.address.zip);
    this.contactForm.get('addressCity').setValue(this.address.townCity);
    this.contactForm.get('addressCountry').setValue(this.address.country);
    this.initInternationalDialogCode();
  }

  initInternationalDialogCode() {
    if (this.countryId !== null) {
      this.countryService.getInternationalDialingCode(this.countryId).subscribe(
        data => {
          if (data !== null) {
            this.internationalDialingCode = '+'.concat(data);
          } else {
            this.internationalDialingCode = '';
          }
        });
    } else {
      this.internationalDialingCode = '';
    }
  }

  save() {
    this.contactForm.markAsDirty();
    Object.keys(this.contactForm.controls).forEach(key => {
      this.contactForm.get(key).markAsDirty();
    });
    if (this.isContactType() === true) {
      this.contactForm.get('value').setValidators(Validators.required);
      this.contactForm.get('value').updateValueAndValidity();
    } else {
      this.contactForm.get('value').clearValidators();
      this.contactForm.get('value').updateValueAndValidity();
    }

    if (this.isContactType() !== true) {
      this.contactForm.get('address1').setValidators(Validators.required);
      this.contactForm.get('address1').updateValueAndValidity();
      this.contactForm.get('addressCity').setValidators(Validators.required);
      this.contactForm.get('addressCity').updateValueAndValidity();
      this.contactForm.get('addressCountry').setValidators(Validators.required);
      this.contactForm.get('addressCountry').updateValueAndValidity();
    } else {
      this.contactForm.get('address1').clearValidators();
      this.contactForm.get('address1').updateValueAndValidity();
      this.contactForm.get('addressCity').clearValidators();
      this.contactForm.get('addressCity').updateValueAndValidity();
      this.contactForm.get('addressCountry').clearValidators();
      this.contactForm.get('addressCountry').updateValueAndValidity();
    }
    if (this.contactForm.invalid) {
      return;
    }
    if (this.isLocationType()) {
      this.address.locationType = this.contactForm.get('contactType').value;
      this.address.address1 = this.contactForm.get('address1').value;
      this.address.address2 = this.contactForm.get('address2').value;
      this.address.address3 = this.contactForm.get('address3').value;
      this.address.address4 = this.contactForm.get('address4').value;
      this.address.zip = this.contactForm.get('addressZip').value;
      this.address.townCity = this.contactForm.get('addressCity').value;
      this.address.country = this.contactForm.get('addressCountry').value;
      this.onSave.emit(this.address);
    } else {
      this.contact.type = this.contactForm.get('contactType').value;
      this.contact.value = this.contactForm.get('value').value;
      this.contact.visibleInDirectory = this.contactForm.get('contactVisibility').value;
      this.contact.preferred = this.contactForm.get('preferred').value;
      this.onSave.emit(this.contact);
    }

    this.display = false;

  }

  isContactType(): boolean {
    return this.contactForm.get('contactType').value
      && !this.isLocationType();
  }


  isLocationType(): boolean {
    return this.includingPostAddress
      && this.locationTypes.find(type => type === this.contactForm.get('contactType').value);
  }

  isChangedContactType(): void {
    if (this.contactForm.get('contactType').value === 'PHONE_NUMBER'
      || this.contactForm.get('contactType').value === 'FAX') {
      this.contactForm.get('value').setValue(this.internationalDialingCode);
    } else {
      this.contactForm.get('value').setValue('');
    }
    this.checkIfIsMorePreferredContactType(this.contactForm.get('contactType').value);
  }

  checkIfIsMorePreferredContactType(contactType: string) {
    for (const contact of this.contacts) {
      if (contact.type === contactType) {
        if (contact.preferred === true) {
          this.contactForm.get('preferred').disable();
          return;
        } else {
          this.contactForm.get('preferred').enable();
        }
      }
    }
  }

  changeCheckBoxPreferredContact() {
    if (this.contactForm.get('preferred').value === true) {
      this.contactForm.get('visibleInDirectory').disable();
    } else {
      this.contactForm.get('visibleInDirectory').enable();
    }
    this.contactForm.get('visibleInDirectory').patchValue(true);
  }
}


